import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import dayjs from 'dayjs';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { plPL } from '@mui/x-data-grid/locales';
import timeTrackerController, { ITimeTrackerScan } from '../../../Api/TimeTracker';
import { useLocalStorageState } from '../utils/useLocalStorageState';

export type AllScansViewProps = {
  onRefreshed?: () => void;
};

type TimeTrackerRow = ITimeTrackerScan & {
  id: string;
  createdAtDate: Date | null;
};

const dateKey = (isoDate: string | Date) => {
  const d = dayjs(isoDate);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const monthNumber = (isoDate: string | Date) => {
  const d = dayjs(isoDate);
  return d.isValid() ? d.month() + 1 : null;
};

const yearNumber = (isoDate: string | Date) => {
  const d = dayjs(isoDate);
  return d.isValid() ? d.year() : null;
};

const chipColorForPlace = (place?: string) => {
  const key = (place || '').toLowerCase();
  if (key === 'sklep') return 'primary' as const;
  if (key === 'magazyn') return 'secondary' as const;
  return 'default' as const;
};

const chipColorForActivity = (activity?: string) => {
  const key = (activity || '').toLowerCase();
  if (key === 'wejście' || key === 'wejscie' || key === 'wejscie/wyjscie' || key === 'wejscie / wyjscie') return 'success' as const;
  if (key === 'przerwa') return 'warning' as const;
  if (key === 'papieros') return 'error' as const;
  if (key === 'piec') return 'info' as const;
  return 'default' as const;
};

const chipColorForPhase = (phase?: string | null) => {
  const key = (phase || '').toLowerCase();
  if (key === 'start') return 'success' as const;
  if (key === 'end') return 'info' as const;
  if (key === 'blocked') return 'warning' as const;
  return 'default' as const;
};

type FiltersState = {
  placeFilter: string;
  activityFilter: string;
  personFilter: string;
  dayFilter: string;
  monthFilter: string;
  yearFilter: string;
  mobileSort: 'newest' | 'oldest' | 'personAsc' | 'personDesc' | 'placeAsc' | 'activityAsc';
  mobileLimit: number;
};

const defaultFilters: FiltersState = {
  placeFilter: '',
  activityFilter: '',
  personFilter: '',
  dayFilter: '',
  monthFilter: '',
  yearFilter: '',
  mobileSort: 'newest',
  mobileLimit: 30,
};

const AllScansView: FC<AllScansViewProps> = ({ onRefreshed }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [scans, setScans] = useState<ITimeTrackerScan[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deletingById, setDeletingById] = useState<Record<string, boolean>>({});

  const [filters, setFilters] = useLocalStorageState<FiltersState>('timeTracker:allScans:filters', defaultFilters);

  const fetchScans = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const res = await timeTrackerController.getAllScans();
    if (!res.ok) {
      setScans([]);
      setError(res.message || 'Nie udało się pobrać skanów');
      setIsLoading(false);
      return;
    }

    setScans(res.scans || []);
    setIsLoading(false);
    onRefreshed?.();
  }, [onRefreshed]);

  const deleteScan = useCallback(async (scanId: string) => {
    const id = String(scanId || '').trim();
    if (!id) return;
    if (deletingById[id]) return;

    const ok = window.confirm('Usunąć ten skan?');
    if (!ok) return;

    setDeletingById((prev) => ({ ...prev, [id]: true }));
    const res = await timeTrackerController.deleteScan(id);
    setDeletingById((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });

    if (!res.ok) {
      window.alert(res.message || 'Nie udało się usunąć skanu');
      return;
    }

    setScans((prev) => prev.filter((s) => s.scanId !== id));
  }, [deletingById]);

  useEffect(() => {
    fetchScans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  type Option = { label: string; value: string };
  const persons: Option[] = useMemo(() => {
    const values = Array.from(
      new Set(scans.map((s) => s.employeeName).filter((v): v is string => Boolean(v && v.trim())))
    ).sort((a, b) => a.localeCompare(b));
    return values.map((v) => ({ label: v, value: v }));
  }, [scans]);

  const places = useMemo(() => {
    return Array.from(new Set(scans.map((s) => s.place).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'pl'));
  }, [scans]);

  const activities = useMemo(() => {
    return Array.from(
      new Set(scans.map((s) => (s.activityLabel || s.activityType || s.type || '').trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b, 'pl'));
  }, [scans]);

  const years = useMemo(() => {
    const values = scans
      .map((s) => yearNumber(s.createdAt))
      .filter((v): v is number => typeof v === 'number');
    return Array.from(new Set(values)).sort((a, b) => b - a);
  }, [scans]);

  const filteredScans = useMemo(() => {
    return scans.filter((s) => {
      if (filters.placeFilter && s.place !== filters.placeFilter) return false;

      const activity = (s.activityLabel || s.activityType || s.type || '').trim();
      if (filters.activityFilter && activity !== filters.activityFilter) return false;

      if (filters.personFilter && (s.employeeName || '') !== filters.personFilter) return false;
      if (filters.dayFilter && dateKey(s.createdAt) !== filters.dayFilter) return false;
      if (filters.monthFilter) {
        const m = monthNumber(s.createdAt);
        if (!m || String(m) !== filters.monthFilter) return false;
      }
      if (filters.yearFilter) {
        const y = yearNumber(s.createdAt);
        if (!y || String(y) !== filters.yearFilter) return false;
      }
      return true;
    });
  }, [scans, filters]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, mobileLimit: 30 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.placeFilter, filters.activityFilter, filters.personFilter, filters.dayFilter, filters.monthFilter, filters.yearFilter]);

  const mobileSortedScans = useMemo(() => {
    const copy = [...filteredScans];
    const compareText = (a: string, b: string) => a.localeCompare(b, 'pl');
    const dt = (s: ITimeTrackerScan) => {
      const d = new Date(s.createdAt);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };

    switch (filters.mobileSort) {
      case 'oldest':
        copy.sort((a, b) => dt(a) - dt(b));
        break;
      case 'personAsc':
        copy.sort((a, b) => compareText(a.employeeName || '', b.employeeName || ''));
        break;
      case 'personDesc':
        copy.sort((a, b) => compareText(b.employeeName || '', a.employeeName || ''));
        break;
      case 'placeAsc':
        copy.sort((a, b) => compareText(a.place || '', b.place || ''));
        break;
      case 'activityAsc':
        copy.sort((a, b) => compareText(a.activityLabel || a.activityType || a.type || '', b.activityLabel || b.activityType || b.type || ''));
        break;
      case 'newest':
      default:
        copy.sort((a, b) => dt(b) - dt(a));
        break;
    }

    return copy;
  }, [filteredScans, filters.mobileSort]);

  const rows = useMemo<TimeTrackerRow[]>(() => {
    return filteredScans.map((s) => ({
      id: s.scanId,
      ...s,
      createdAtDate: s.createdAt ? new Date(s.createdAt) : null,
    }));
  }, [filteredScans]);

  const columns: GridColDef<TimeTrackerRow>[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Tooltip title="Usuń skan">
            <span>
              <IconButton
                size="small"
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  deleteScan(String(params.row.scanId));
                }}
                disabled={Boolean(deletingById[String(params.row.scanId)])}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' },
                }}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
      { field: 'createdAtDate', headerName: 'createdAt', type: 'dateTime', width: 200, hideable: true },
      {
        field: 'employeeName',
        headerName: 'Pracownik',
        flex: 1.2,
        minWidth: 220,
        valueGetter: (_value, row) => row.employeeName || 'Nieprzypisana karta',
      },
      {
        field: 'createdAt',
        headerName: 'Data',
        width: 120,
        valueGetter: (_value, row) => (row.createdAt ? dayjs(row.createdAt).format('YYYY-MM-DD') : '-'),
      },
      { field: 'time', headerName: 'Godzina', width: 110 },
      {
        field: 'place',
        headerName: 'Miejsce',
        width: 140,
        renderCell: (params) => (
          <Chip size="small" variant="filled" color={chipColorForPlace(params.value)} label={params.value || '-'} />
        ),
      },
      {
        field: 'activityLabel',
        headerName: 'Aktywność',
        width: 160,
        valueGetter: (_value, row) => row.activityLabel || row.activityType || row.type || '-',
        renderCell: (params) => (
          <Chip
            size="small"
            variant="outlined"
            color={chipColorForActivity(String(params.value || ''))}
            label={params.value || '-'}
          />
        ),
      },
      {
        field: 'actionPhase',
        headerName: 'Start/Stop',
        width: 130,
        renderCell: (params) => (
          <Chip size="small" variant="filled" color={chipColorForPhase(params.value as any)} label={params.value || '-'} />
        ),
      },
      {
        field: 'duration',
        headerName: 'Czas',
        width: 110,
        valueGetter: (_value, row) => (row.actionPhase === 'end' ? row.duration || '-' : '-'),
      },
      { field: 'uid', headerName: 'UID', width: 180, sortable: false },
      { field: 'device_id', headerName: 'device_id', width: 120, sortable: false, hideable: true },
      { field: 'station_id', headerName: 'station_id', width: 120, sortable: false, hideable: true },
    ],
    [deleteScan, deletingById]
  );

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} gap={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={800}>
              Wszystkie skany
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Wynik: {rows.length} / {scans.length}
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              onClick={fetchScans}
              disabled={isLoading}
              fullWidth={!isDesktop}
            >
              Odśwież
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearAllIcon />}
              onClick={clearFilters}
              fullWidth={!isDesktop}
            >
              Wyczyść filtry
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} sx={{ mb: 2 }}>
          <Autocomplete
            options={persons}
            value={persons.find((p) => p.value === filters.personFilter) || null}
            onChange={(_, val) => setFilters((prev) => ({ ...prev, personFilter: val?.value || '' }))}
            renderInput={(params) => <TextField {...params} label="Pracownik" placeholder="Wybierz…" />}
            sx={{ minWidth: 260, flex: 1 }}
            clearOnEscape
          />

          <TextField
            select
            label="Miejsce"
            value={filters.placeFilter}
            onChange={(e) => setFilters((prev) => ({ ...prev, placeFilter: e.target.value }))}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {places.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Aktywność"
            value={filters.activityFilter}
            onChange={(e) => setFilters((prev) => ({ ...prev, activityFilter: e.target.value }))}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {activities.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Dzień"
            type="date"
            value={filters.dayFilter}
            onChange={(e) => setFilters((prev) => ({ ...prev, dayFilter: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />

          <TextField
            select
            label="Miesiąc"
            value={filters.monthFilter}
            onChange={(e) => setFilters((prev) => ({ ...prev, monthFilter: e.target.value }))}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {Array.from({ length: 12 }).map((_, idx) => {
              const m = idx + 1;
              return (
                <MenuItem key={m} value={String(m)}>
                  {String(m).padStart(2, '0')}
                </MenuItem>
              );
            })}
          </TextField>

          <TextField
            select
            label="Rok"
            value={filters.yearFilter}
            onChange={(e) => setFilters((prev) => ({ ...prev, yearFilter: e.target.value }))}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            {years.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {isDesktop ? (
          <Box sx={{ height: 720, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              localeText={plPL.components.MuiDataGrid.defaultProps.localeText}
              disableRowSelectionOnClick
              loading={isLoading}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 50, page: 0 } },
                sorting: { sortModel: [{ field: 'createdAtDate', sort: 'desc' }] },
                columns: {
                  columnVisibilityModel: {
                    createdAtDate: false,
                    device_id: false,
                    station_id: false,
                  },
                },
              }}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 250 },
                },
              }}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiDataGrid-columnHeaders': { backgroundColor: 'rgba(0,0,0,0.03)' },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
              }}
            />
          </Box>
        ) : (
          <Box>
            <Accordion defaultExpanded={false} sx={{ mb: 1.5, borderRadius: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={700}>Ustawienia widoku</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                  <TextField
                    select
                    label="Sortowanie"
                    value={filters.mobileSort}
                    onChange={(e) => setFilters((prev) => ({ ...prev, mobileSort: e.target.value as any }))}
                    fullWidth
                  >
                    <MenuItem value="newest">Najnowsze</MenuItem>
                    <MenuItem value="oldest">Najstarsze</MenuItem>
                    <MenuItem value="personAsc">Pracownik A→Z</MenuItem>
                    <MenuItem value="personDesc">Pracownik Z→A</MenuItem>
                    <MenuItem value="placeAsc">Miejsce A→Z</MenuItem>
                    <MenuItem value="activityAsc">Aktywność A→Z</MenuItem>
                  </TextField>

                  <TextField
                    select
                    label="Pokaż"
                    value={String(filters.mobileLimit)}
                    onChange={(e) => setFilters((prev) => ({ ...prev, mobileLimit: Number(e.target.value) }))}
                    fullWidth
                  >
                    {[30, 60, 120, 300].map((n) => (
                      <MenuItem key={n} value={String(n)}>
                        {n}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Stack gap={1.2}>
              {mobileSortedScans.slice(0, filters.mobileLimit).map((s) => {
                const name = s.employeeName || 'Nieprzypisana karta';
                const activity = (s.activityLabel || s.activityType || s.type || '').trim();
                const isDeleting = Boolean(deletingById[String(s.scanId)]);

                return (
                  <Paper
                    key={s.scanId}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={800} sx={{ lineHeight: 1.1 }} noWrap>
                          {name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.createdAt ? `${dayjs(s.createdAt).format('YYYY-MM-DD')} • ${s.time || dayjs(s.createdAt).format('HH:mm')}` : s.time || '-'}
                        </Typography>
                      </Box>

                      <Stack direction="row" gap={0.75} flexWrap="wrap" justifyContent="flex-end" alignItems="center">
                        <Chip size="small" variant="filled" color={chipColorForPlace(s.place)} label={s.place || '-'} />
                        <Chip
                          size="small"
                          variant="outlined"
                          color={chipColorForActivity(activity)}
                          label={activity || '-'}
                        />
                        <Chip
                          size="small"
                          variant="filled"
                          color={chipColorForPhase(s.actionPhase || null)}
                          label={s.actionPhase || '-'}
                        />
                        {s.actionPhase === 'end' && s.duration ? (
                          <Chip size="small" variant="outlined" label={`Czas: ${s.duration}`} />
                        ) : null}

                        <Tooltip title="Usuń skan">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => deleteScan(String(s.scanId))}
                              disabled={isDeleting}
                              sx={{
                                color: 'text.secondary',
                                '&:hover': { color: 'error.main' },
                              }}
                            >
                              <DeleteOutlineRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1.25 }} />

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary" noWrap>
                        UID: {s.uid}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {String(s.device_id || '')}/{String(s.station_id || '')}
                      </Typography>
                    </Stack>
                  </Paper>
                );
              })}

              {mobileSortedScans.length > filters.mobileLimit ? (
                <Button
                  variant="outlined"
                  onClick={() => setFilters((prev) => ({ ...prev, mobileLimit: prev.mobileLimit + 30 }))}
                  sx={{ mt: 0.5, borderRadius: 3 }}
                >
                  Pokaż więcej
                </Button>
              ) : null}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AllScansView;